import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Paper, Box, Button, List, ListItem, ListItemText, ListItemIcon,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, Table, 
  TableBody, TableCell, TableHead, TableRow, Tabs, Tab, Grid, Card, CardContent,
  Select, MenuItem, FormControl, InputLabel, IconButton, LinearProgress, Checkbox,
  Accordion, AccordionSummary, AccordionDetails, Chip, Avatar, Divider, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Edit Mode State
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  // Team State
  const [team, setTeam] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignUser, setAssignUser] = useState('');
  const [assignRole, setAssignRole] = useState('Member');

  // Report State
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [newReportName, setNewReportName] = useState('');
  const [createReportLoading, setCreateReportLoading] = useState(false);

  // Documents State
  const [documents, setDocuments] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);

  // Member Details State
  const [openMemberDialog, setOpenMemberDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberHours, setMemberHours] = useState([]);

  // Milestones State
  const [milestones, setMilestones] = useState([]);
  const [openMilestoneDialog, setOpenMilestoneDialog] = useState(false);
  const [newMilestoneName, setNewMilestoneName] = useState('');
  const [newMilestoneDate, setNewMilestoneDate] = useState('');

  // QR Dialog State
  const [qrDialog, setQrDialog] = useState({ open: false, url: '', title: '' });

  // SubReport Upload State
  const [uploadSubReportId, setUploadSubReportId] = useState(null);
  const fileInputRef = React.useRef(null);

  const token = localStorage.getItem('token');

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(res.data);
      setEditData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeam(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/setup/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMilestones = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMilestones(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchTeam();
    fetchAllUsers();
    fetchDocuments();
    fetchMilestones();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // --- Edit Project ---
  const handleSaveProject = async () => {
    try {
      await axios.put(`${API_URL}/projects/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditMode(false);
      fetchProject();
    } catch (err) {
      console.error(err);
      alert("Error updating project");
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setEditData({
            ...editData,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Error getting location. Please ensure location services are enabled.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const handleInputChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  // --- Team Management ---
  const handleAssignUser = async () => {
    try {
      await axios.post(`${API_URL}/projects/${id}/team`, {
        user_id: assignUser,
        role: assignRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenAssignDialog(false);
      fetchTeam();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error assigning user");
    }
  };

  const handleRemoveUser = async (userId) => {
    if(!window.confirm("Are you sure you want to remove this user?")) return;
    try {
      await axios.delete(`${API_URL}/projects/${id}/team?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTeam();
    } catch (err) {
      console.error(err);
      alert("Error removing user");
    }
  };

  // --- Reports ---
  const handleAddReport = async () => {
    setCreateReportLoading(true);
    try {
      await axios.post(`${API_URL}/projects/${id}/reports`, { name: newReportName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenReportDialog(false);
      setNewReportName('');
      fetchProject(); 
    } catch (err) {
      console.error(err);
    } finally {
      setCreateReportLoading(false);
    }
  };

  const handleAddSubReport = async (reportId) => {
    try {
        await axios.post(`${API_URL}/reports/${reportId}/subreports`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        fetchProject();
    } catch (err) {
        console.error(err);
        alert("Error creating subreport");
    }
  };

  // --- Documents ---
  const handleUploadDocument = async () => {
    if (!uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      await axios.post(`${API_URL}/projects/${id}/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadFile(null);
      fetchDocuments();
    } catch (err) {
      console.error(err);
      alert("Error uploading file");
    }
  };

  // --- Milestones ---
  const handleAddMilestone = async () => {
    try {
      await axios.post(`${API_URL}/projects/${id}/milestones`, {
        name: newMilestoneName,
        due_date: newMilestoneDate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenMilestoneDialog(false);
      setNewMilestoneName('');
      setNewMilestoneDate('');
      fetchMilestones();
    } catch (err) {
      console.error(err);
      alert("Error creating milestone");
    }
  };

  const handleToggleMilestone = async (milestoneId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/milestones/${milestoneId}`, {
        completed: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMilestones();
    } catch (err) {
      console.error(err);
      alert("Error updating milestone");
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if(!window.confirm("Delete this milestone?")) return;
    try {
      await axios.delete(`${API_URL}/milestones/${milestoneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMilestones();
    } catch (err) {
      console.error(err);
      alert("Error deleting milestone");
    }
  };

  const handleViewQR = (url, title) => {
    setQrDialog({ open: true, url, title });
  };

  const handleMemberClick = async (member) => {
    setSelectedMember(member);
    try {
        const res = await axios.get(`${API_URL}/projects/${id}/team/${member.user_id}/hours`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setMemberHours(res.data);
        setOpenMemberDialog(true);
    } catch (err) {
        console.error(err);
        alert("Failed to fetch member hours");
    }
  };

  const handleUploadClick = (subReportId) => {
    setUploadSubReportId(subReportId);
    if (fileInputRef.current) {
        fileInputRef.current.click();
    }
  };

  const handleSubReportFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadSubReportId) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
        await axios.post(`${API_URL}/subreports/${uploadSubReportId}/upload`, formData, {
            headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        // Reset and refresh
        setUploadSubReportId(null);
        event.target.value = null; // Reset file input
        fetchProject();
        alert("File uploaded successfully");
    } catch (err) {
        console.error(err);
        alert("Error uploading file");
    }
  };

  const handleDownloadSubReport = (subReportId) => {
      const url = `${API_URL}/subreports/${subReportId}/download`;
      window.open(url, '_blank');
  };

  const handleDownloadDocument = (filename) => {
      const encodedFilename = encodeURIComponent(filename);
      const url = `${API_URL}/projects/${id}/documents/${encodedFilename}`;
      window.open(url, '_blank');
  };

  if (loading || !project) return <Loading message="Loading Project Details..." />;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/projects')} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {project.name}
                </Typography>
                <Chip 
                  label={project.status} 
                  sx={{ 
                    bgcolor: 'white', 
                    color: 'primary.main', 
                    fontWeight: 'bold',
                    height: 24
                  }} 
                />
              </Box>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Ref: {project.ref}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
              onClick={() => editMode ? handleSaveProject() : setEditMode(true)}
              startIcon={editMode ? <SaveIcon /> : <EditIcon />}
            >
              {editMode ? "Save" : "Edit"}
            </Button>
            <Button 
              variant="outlined" 
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              startIcon={<QrCodeIcon />}
              onClick={() => handleViewQR(`${API_URL}/projects/${id}/qr`, project.name)}
            >
              QR Code
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column: Main Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', px: 2 }}
            >
              <Tab icon={<AssignmentIcon />} label="Overview" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<PersonAddIcon />} label="Team" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<InsertDriveFileIcon />} label="Reports" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<CloudUploadIcon />} label="Documents" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<EventIcon />} label="Milestones" iconPosition="start" sx={{ minHeight: 64 }} />
            </Tabs>

            {/* Overview Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Description Section */}
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Project Description</Typography>
                  {editMode ? (
                    <TextField 
                      fullWidth multiline rows={4} 
                      name="description" 
                      value={editData.description} 
                      onChange={handleInputChange} 
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {project.description || "No description provided."}
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* Info Cards Grid */}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Client Information</Typography>
                      <Typography variant="h6" fontWeight="medium">{project.client || 'N/A'}</Typography>
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Quote Ref:</Typography>
                        {editMode ? (
                          <TextField size="small" name="quote_ref" value={editData.quote_ref || ''} onChange={handleInputChange} />
                        ) : (
                          <Chip label={project.quote_ref || 'N/A'} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Project Health</Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Box>
                          <Typography variant="caption" display="block">Status</Typography>
                          {editMode ? (
                            <Select size="small" name="status" value={editData.status} onChange={handleInputChange} sx={{ minWidth: 120 }}>
                              <MenuItem value="In Progress">In Progress</MenuItem>
                              <MenuItem value="Submitted">Submitted</MenuItem>
                              <MenuItem value="Under Review">Under Review</MenuItem>
                              <MenuItem value="Overdue">Overdue</MenuItem>
                              <MenuItem value="Completed">Completed</MenuItem>
                            </Select>
                          ) : (
                            <Typography variant="body1" fontWeight="medium">{project.status}</Typography>
                          )}
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box>
                          <Typography variant="caption" display="block">Priority</Typography>
                          {editMode ? (
                            <Select size="small" name="priority" value={editData.priority} onChange={handleInputChange} sx={{ minWidth: 100 }}>
                              <MenuItem value="Low">Low</MenuItem>
                              <MenuItem value="Medium">Medium</MenuItem>
                              <MenuItem value="High">High</MenuItem>
                            </Select>
                          ) : (
                            <Typography variant="body1" fontWeight="medium" color={project.priority === 'High' ? 'error.main' : 'text.primary'}>
                              {project.priority}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Financial Overview</Typography>
                        <Chip 
                          label={`${Math.round((project.spent / project.budget) * 100) || 0}% Spent`} 
                          size="small" 
                          color={project.spent > project.budget ? 'error' : 'default'} 
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total Budget</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoneyIcon color="primary" fontSize="small" />
                            {editMode ? (
                              <TextField size="small" type="number" name="budget" value={editData.budget} onChange={handleInputChange} sx={{ width: 120 }} />
                            ) : (
                              <Typography variant="h6">${project.budget?.toLocaleString()}</Typography>
                            )}
                          </Box>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total Spent</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoneyIcon color="error" fontSize="small" />
                            {editMode ? (
                              <TextField size="small" type="number" name="spent" value={editData.spent} onChange={handleInputChange} sx={{ width: 120 }} />
                            ) : (
                              <Typography variant="h6">${project.spent?.toLocaleString()}</Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ width: '100%', bgcolor: 'grey.100', borderRadius: 1, height: 8, overflow: 'hidden' }}>
                        <Box 
                          sx={{ 
                            width: `${Math.min((project.spent / project.budget) * 100, 100)}%`, 
                            bgcolor: project.spent > project.budget ? 'error.main' : 'success.main',
                            height: '100%'
                          }} 
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Location Map */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                        {editMode && (
                          <Button 
                            size="small" 
                            startIcon={<MyLocationIcon />} 
                            onClick={getLocation}
                          >
                            Get Location
                          </Button>
                        )}
                      </Box>
                      
                      {editMode ? (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Latitude"
                              name="latitude"
                              type="number"
                              value={editData.latitude || ''}
                              onChange={handleInputChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Longitude"
                              name="longitude"
                              type="number"
                              value={editData.longitude || ''}
                              onChange={handleInputChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      ) : (
                        project.latitude && project.longitude ? (
                          <Box sx={{ width: '100%', height: 300, borderRadius: 2, overflow: 'hidden', mt: 1 }}>
                            <MapContainer center={[project.latitude, project.longitude]} zoom={13} style={{ height: '100%', width: '100%' }}>
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <Marker position={[project.latitude, project.longitude]} />
                            </MapContainer>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No location data available.
                          </Typography>
                        )
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>

            {/* Team Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Team Members</Typography>
                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenAssignDialog(true)}>
                  Add Member
                </Button>
              </Box>
              <Grid container spacing={2}>
                {team.map((member) => (
                  <Grid item xs={12} sm={6} key={member.user_id}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.50', borderColor: 'primary.main' }
                      }}
                      onClick={() => handleMemberClick(member)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 'bold' }}>{member.name[0]}</Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">{member.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip label={member.role} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            <Chip label={`${member.total_hours || 0} hrs`} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                          </Box>
                        </Box>
                      </Box>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleRemoveUser(member.user_id); 
                        }} 
                        sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', '&:hover': { bgcolor: 'error.main', color: 'white' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Grid>
                ))}
                {team.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                      <Typography color="textSecondary">No team members assigned yet.</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* Reports Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Project Reports</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenReportDialog(true)}>
                  New Report
                </Button>
              </Box>
              
              {project.reports && project.reports.map((report) => (
                <Accordion key={report.id} disableGutters elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <InsertDriveFileIcon color="action" />
                        <Typography variant="subtitle1" fontWeight="bold">{report.name}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                        {report.created_at}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddSubReport(report.id)}>
                        Add Revision
                      </Button>
                    </Box>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>ID</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Revision</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.subreports.map(sub => (
                          <TableRow key={sub.id} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{sub.generated_id}</TableCell>
                            <TableCell>
                              <Chip label={`Rev ${sub.revision}`} size="small" color="primary" variant="outlined" sx={{ borderRadius: 1 }} />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Tooltip title="View QR">
                                  <IconButton size="small" onClick={() => handleViewQR(`${API_URL}/subreports/${sub.generated_id}/qr`, sub.generated_id)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                                    <QrCodeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Upload PDF">
                                  <IconButton size="small" onClick={() => handleUploadClick(sub.generated_id)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                                    <CloudUploadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download PDF">
                                  <span>
                                    <IconButton size="small" disabled={!sub.has_file} onClick={() => handleDownloadSubReport(sub.generated_id)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {report.subreports.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                              No revisions yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
              {(!project.reports || project.reports.length === 0) && (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                  <Typography color="textSecondary">No reports created yet.</Typography>
                </Box>
              )}
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ p: 4, border: '2px dashed', borderColor: 'primary.light', borderRadius: 3, textAlign: 'center', mb: 4, bgcolor: 'grey.50' }}>
                <input
                  accept="*/*"
                  style={{ display: 'none' }}
                  id="raised-button-file"
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
                <label htmlFor="raised-button-file">
                  <Button variant="contained" component="span" startIcon={<CloudUploadIcon />} size="large">
                    Select File to Upload
                  </Button>
                </label>
                {uploadFile && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 2, border: '1px solid', borderColor: 'divider' }}>
                    <InsertDriveFileIcon color="action" />
                    <Typography variant="body2" fontWeight="medium">{uploadFile.name}</Typography>
                    <Button variant="contained" size="small" onClick={handleUploadDocument}>Upload Now</Button>
                  </Box>
                )}
              </Box>
              
              <Typography variant="h6" fontWeight="bold" gutterBottom>Uploaded Documents</Typography>
              <Grid container spacing={2}>
                {documents.map((doc, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                          <InsertDriveFileIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2" noWrap title={doc} sx={{ fontWeight: 'medium' }}>{doc}</Typography>
                      </Box>
                      <IconButton onClick={() => handleDownloadDocument(doc)} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Grid>
                ))}
                {documents.length === 0 && (
                  <Grid item xs={12}>
                    <Typography color="textSecondary" align="center" sx={{ py: 2 }}>No documents uploaded yet.</Typography>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* Milestones Tab */}
            <TabPanel value={tabValue} index={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Project Milestones</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenMilestoneDialog(true)}>
                  Add Milestone
                </Button>
              </Box>
              <List sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                {milestones.map((milestone, index) => (
                  <ListItem 
                    key={milestone.id} 
                    divider={index !== milestones.length - 1}
                    sx={{ 
                      bgcolor: milestone.completed ? 'grey.50' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox 
                        edge="start"
                        checked={milestone.completed}
                        onChange={() => handleToggleMilestone(milestone.id, milestone.completed)}
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle1" sx={{ textDecoration: milestone.completed ? 'line-through' : 'none', color: milestone.completed ? 'text.secondary' : 'text.primary', fontWeight: milestone.completed ? 'normal' : 'medium' }}>
                          {milestone.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <EventIcon fontSize="inherit" color="action" />
                          <Typography variant="caption" color="text.secondary">Due: {milestone.due_date || 'No date'}</Typography>
                        </Box>
                      }
                    />
                    <IconButton onClick={() => handleDeleteMilestone(milestone.id)} color="error" size="small" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
                {milestones.length === 0 && (
                  <ListItem>
                    <ListItemText primary={<Typography color="textSecondary" align="center">No milestones defined.</Typography>} />
                  </ListItem>
                )}
              </List>
            </TabPanel>
          </Paper>
        </Grid>

        {/* Right Column: Summary/Stats */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Progress Tracking</Typography>
            <Box sx={{ mt: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" fontWeight="medium">Completion</Typography>
                <Typography variant="body2" color="primary.main" fontWeight="bold">{Math.round(project.progress)}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Number(project.progress)} 
                sx={{ height: 10, borderRadius: 5, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { borderRadius: 5 } }} 
              />
            </Box>
            {editMode && (
              <TextField 
                fullWidth type="number" label="Update Progress %" name="progress" 
                value={editData.progress} onChange={handleInputChange} size="small" sx={{ mb: 2 }} 
              />
            )}
          </Paper>

          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Key Dates</Typography>
            <List disablePadding>
              <ListItem sx={{ px: 0, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <ListItemIcon sx={{ minWidth: 40 }}><EventIcon color="action" /></ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body2" color="text.secondary">Due Date</Typography>}
                  secondary={<Typography variant="body1" fontWeight="medium">{project.due_date || "Not set"}</Typography>} 
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}><EventIcon color="action" /></ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body2" color="text.secondary">Created On</Typography>}
                  secondary={<Typography variant="body1" fontWeight="medium">{project.created_at}</Typography>} 
                />
              </ListItem>
            </List>
            {editMode && (
              <TextField 
                fullWidth type="date" label="Update Due Date" name="due_date" 
                value={editData.due_date} onChange={handleInputChange} InputLabelProps={{ shrink: true }} size="small" sx={{ mt: 2 }} 
              />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog open={openReportDialog} onClose={() => setOpenReportDialog(false)}>
        <DialogTitle>Add New Report</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Report Name" fullWidth
            value={newReportName} onChange={(e) => setNewReportName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportDialog(false)}>Cancel</Button>
          <Button onClick={handleAddReport} variant="contained" disabled={createReportLoading}>
            {createReportLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)}>
        <DialogTitle>Assign User</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel>User</InputLabel>
            <Select value={assignUser} onChange={(e) => setAssignUser(e.target.value)} label="User">
              {allUsers.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.full_name} ({u.role})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select value={assignRole} onChange={(e) => setAssignRole(e.target.value)} label="Role">
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Lead">Lead</MenuItem>
              <MenuItem value="Member">Member</MenuItem>
              <MenuItem value="Viewer">Viewer</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMilestoneDialog} onClose={() => setOpenMilestoneDialog(false)}>
        <DialogTitle>Add Milestone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Milestone Name" fullWidth
            value={newMilestoneName} onChange={(e) => setNewMilestoneName(e.target.value)}
          />
          <TextField
            margin="dense" label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
            value={newMilestoneDate} onChange={(e) => setNewMilestoneDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMilestoneDialog(false)}>Cancel</Button>
          <Button onClick={handleAddMilestone} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Member Hours Dialog */}
      <Dialog open={openMemberDialog} onClose={() => setOpenMemberDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
            {selectedMember?.name} - Work Log
        </DialogTitle>
        <DialogContent>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Duration (Hrs)</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {memberHours.map((log) => (
                        <TableRow key={log.id}>
                            <TableCell>{log.date}</TableCell>
                            <TableCell>{log.start_time} - {log.end_time}</TableCell>
                            <TableCell>{log.duration}</TableCell>
                            <TableCell>{log.type_of_work}</TableCell>
                            <TableCell>{log.description}</TableCell>
                        </TableRow>
                    ))}
                    {memberHours.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} align="center">No work logs found.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenMemberDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qrDialog.open} onClose={() => setQrDialog({ ...qrDialog, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>{qrDialog.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <img 
              src={qrDialog.url} 
              alt="QR Code" 
              style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, border: '1px solid #eee' }} 
              onError={(e) => {e.target.onerror = null; e.target.src="https://via.placeholder.com/150?text=No+QR"}}
            />
          </Box>
          <Typography variant="caption" display="block" align="center" color="textSecondary">
            Scan to view details
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setQrDialog({ ...qrDialog, open: false })} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input for subreport uploads */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="application/pdf"
        onChange={handleSubReportFileChange}
      />

    </Container>
  );
}

export default ProjectDetails;
